import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/Button';
import { ProjectCard } from '../components/projects/ProjectCard';
import { projectService, Project } from '../services/projectService';

export const Home: React.FC = () => {
  const [featuredProjects, setFeaturedProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedProjects = async () => {
      try {
        const response = await projectService.getProjects({
          limit: 3,
          page: 1,
          status: 'active',
          sortBy: 'createdAt',
          sortOrder: 'desc'
        });
        setFeaturedProjects(response.projects);
      } catch (error) {
        console.error('Error fetching featured projects:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedProjects();
  }, []);

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-600 via-accent-500 to-primary-700 animate-gradient"></div>
        <div className="relative max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Where Side Projects Find Their Next Chapter
            </h1>
            <p className="mt-6 text-xl text-primary-100 max-w-3xl mx-auto">
              Join a community of indie developers buying and selling micro startups, side projects, and SaaS apps.
              Built by devs, for devs.
            </p>
            <div className="mt-10 flex justify-center space-x-4">
              <Link to="/projects">
                <Button 
                  className="px-8 py-3 text-lg font-medium bg-transparent text-white border-2 border-white hover:bg-white hover:text-primary-600"
                >
                  Browse Projects
                </Button>
              </Link>
              <Link to="/sell-your-project">
                <Button 
                  className="px-8 py-3 text-lg font-medium bg-transparent text-white border-2 border-white hover:bg-white hover:text-primary-600"
                >
                  List Your Project
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Projects Section */}
      <div className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-base font-semibold text-primary-600 tracking-wide uppercase">Featured Projects</h2>
            <p className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Check out these indie gems
            </p>
          </div>

          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {loading ? (
              // Loading skeleton
              [...Array(3)].map((_, index) => (
                <div key={index} className="bg-white rounded-xl shadow-soft overflow-hidden animate-pulse">
                  <div className="relative w-full pt-[56.25%] bg-gray-200"></div>
                  <div className="p-6">
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                    <div className="h-4 bg-gray-200 rounded w-full mb-4"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                  </div>
                </div>
              ))
            ) : (
              featuredProjects.map((project) => (
                <ProjectCard key={project._id} {...project} />
              ))
            )}
          </div>

          <div className="mt-12 text-center">
            <Link to="/projects">
              <Button className="shadow-soft">View All Projects</Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-base font-semibold text-primary-600 tracking-wide uppercase">Why ForgeFair?</h2>
            <p className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Built for the indie dev community
            </p>
          </div>

          <div className="mt-20">
            <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-3">
              {/* Feature 1 */}
              <div className="relative p-6 bg-white rounded-lg shadow-soft hover:shadow-lg transition-shadow duration-200">
                <div className="text-lg font-medium text-gray-900">Fair & Transparent</div>
                <p className="mt-2 text-base text-gray-500">
                  No hidden fees or complicated processes. Just straightforward deals between developers who understand the value of side projects.
                </p>
              </div>

              {/* Feature 2 */}
              <div className="relative p-6 bg-white rounded-lg shadow-soft hover:shadow-lg transition-shadow duration-200">
                <div className="text-lg font-medium text-gray-900">Community First</div>
                <p className="mt-2 text-base text-gray-500">
                  Connect with fellow indie hackers, share experiences, and learn from each other's journeys.
                </p>
              </div>

              {/* Feature 3 */}
              <div className="relative p-6 bg-white rounded-lg shadow-soft hover:shadow-lg transition-shadow duration-200">
                <div className="text-lg font-medium text-gray-900">Developer-Friendly</div>
                <p className="mt-2 text-base text-gray-500">
                  Built by developers, for developers. We understand tech stacks, code quality, and what makes a project valuable.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-gradient-to-r from-primary-600 to-accent-500 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            <div className="text-center">
              <div className="text-4xl font-bold text-white">100+</div>
              <div className="mt-2 text-sm text-primary-100">Active Projects</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-white">50+</div>
              <div className="mt-2 text-sm text-primary-100">Successful Sales</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-white">1k+</div>
              <div className="mt-2 text-sm text-primary-100">Community Members</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-white">24h</div>
              <div className="mt-2 text-sm text-primary-100">Avg. Response Time</div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <h2 className="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
            <span className="block">Ready to dive in?</span>
            <span className="block text-primary-600">Start your project's journey today.</span>
          </h2>
          <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0">
            <div className="inline-flex rounded-md shadow-soft">
              <Link to="/sell-your-project">
                <Button className="bg-primary-600 text-white hover:bg-primary-700">
                  List Your Project
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 